import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { Stage, Layer, Rect, Group, Text, Line } from 'react-konva';
import { ensureAlfanumericCharacter, i18nIntl, isNotEmpty, waitForElementInDOM, isColorBright } from '../../lib/ep.js';

import { generateGrid, getGridMinSize, getTwinLetters } from '../../ts/games/crosswords.ts';

const standard_board_size = 600;	// px

const WordIndex = ({ index, ...props }) => {
	const scale = props?.scale ?? 1;
	const color = props?.color ?? 'black';

	return (
		<Text
			text={index}
			fontSize={11 * scale}
			fill={color}
			x={2}
			y={3}
		/>
	)
};

const WordBg = ({ word, orientation, refs, ...props}) => {
	const cell_size = props?.cell_size ?? 20;

	const ref = `${orientation}_${word.key_index}`;

	const has_hint = word?.is_hint ?? false;

	const fill_color = props?.color ?? 'rgba(255, 165, 0, 1)';
	const is_fill_color_bright = (isColorBright(main_color)) ? '0, 0, 0, 0.2' : '255, 255, 255, 0.2';

	return (
		<>
			<Group
				x={(word?.x ?? 0) * cell_size}
				y={(word?.y ?? 0) * cell_size}
			>
				{[...Array(word.word.length).keys()].map((letter_index, l) => {
					const is_hint = (has_hint !== false && has_hint == l) ? true : false;
					if(!is_hint) return null;

					return (
						<Rect
							key={`hint_whole_item_${word.key_index}_${l}`}
							x={((orientation == 'h') ? l * cell_size : 0)}
							y={((orientation == 'v') ? l * cell_size : 0)}
							width={cell_size}
							height={cell_size}
							fill={props.ok_bg_color}
							opacity={1}
						/>
					)
				})}

				<Group
					ref={(node) => { refs[`word_bg_${ref}`] = node; }}
					opacity={0}
				>
					{[...Array(word.word.length).keys()].map((letter_index, l) => {
						const is_hint = (has_hint !== false && has_hint == l) ? true : false;

						return (
							<Rect
								key={`whole_item_${word.key_index}_${l}`}
								ref={(node) => { refs[`letter_${orientation}_${word.key_index}_${l}_bg`] = node; }}
								x={((orientation == 'h') ? l * cell_size : 0)}
								y={((orientation == 'v') ? l * cell_size : 0)}
								width={cell_size}
								height={cell_size}
								fill={(!is_hint) ? fill_color : `rgba(${is_fill_color_bright})`}
								opacity={(!is_hint) ? 1 : 0.2}
							/>
						)
					})}
				</Group>
			</Group>
		</>
	)
};

const Word = ({ word, orientation, refs, ...props }) => {
	const cell_size = props?.cell_size ?? 20;

	let acc = 0;

	const ref = `${orientation}_${word.key_index}`;
	const scale = props?.scale ?? 1;

	const has_hint = word?.is_hint ?? false;

	const twins = props?.twins ?? false;
	const word_starts = props?.word_starts ?? false;
	const is_presolved = props?.is_presolved ?? false;
	const display_validations = props?.display_validations ?? false;

	let is_first_twin = false;

	return (
		<Group
			ref={(node) => { refs[`word_${ref}`] = node; }}
			x={(word?.x ?? 0) * cell_size}
			y={(word?.y ?? 0) * cell_size}
			total_letters={word.word.length}
			used_ref={ref}
			word_index={word.word_index}
			expected={word.word}
			is_ok={false}
		>
			{[...Array(word.word.length).keys()].map((letter_index, l) => {
				const letter = word.word[letter_index];

				const col = (word?.x ?? 0) + ((orientation == 'h') ? letter_index : 0);
				const row = (word?.y ?? 0) + ((orientation == 'v') ? letter_index : 0);

				const is_twin = (twins !== false && twins.length > 0) ? ((twins[row][col] == 2) ? true : false) : false;
				const is_word_start = (word_starts !== false && word_starts.length > 0) ? ((word_starts[row][col] == 2) ? true : false) : false;
				const is_hint = (has_hint !== false && has_hint == l) ? true : false;

				if(is_word_start && l == 0 && orientation == 'v') is_first_twin = true;

				return (
					<Group
						key={`whole_item_${word.key_index}_${l}`}
						ref={(node) => { refs[`_letter_${orientation}_${col}_${row}`] = node; }}
						x={((orientation == 'h') ? l * cell_size : 0) + cell_size / 2}
						y={((orientation == 'v') ? l * cell_size : 0) + cell_size / 2}
						text_ref={`letter_${orientation}_${word.key_index}_${l}`}
						offsetX={cell_size / 2}
						offsetY={cell_size / 2}
						is_letter={true}
						onClick={() => props.toggleSelectWord(word.key_index, orientation, l)}
						onTap={() => props.toggleSelectWord(word.key_index, orientation, l)}
						opacity={(!is_twin || orientation == 'h') ? ((is_hint && !display_validations) ? 0.5 : 1) : 0}
					>
						{(!display_validations && is_hint) && (
							<Rect
								width={cell_size}
								height={cell_size}
								fill={props?.text_color ?? 'black'}
								opacity={0.4}
							/>
						)}

						<Text
							ref={(node) => { refs[`letter_${orientation}_${word.key_index}_${l}`] = node; }}
							width={cell_size}
							height={cell_size}
							y={0.1 * cell_size}
							text={(is_hint || is_presolved) ? letter.toUpperCase() : ""}
							needs={letter.toUpperCase()}
							is_hint={is_hint}
							orientation={orientation}
							col={col}
							row={row}
							name='text_letter'
							fontSize={20 * scale}
							wrap='char'
							fill={(!is_hint || !display_validations) ? (props?.text_color ?? 'black') : props.ok_text_color}
							align='center'
							verticalAlign='middle'
							is_ok={false}
						/>
					</Group>
				)
			})}

			{(!is_first_twin) && (
				<WordIndex
					index={word.word_index}
					color={props?.text_color ?? 'black'}
					bg_color={props?.bg_color ?? 'white'}
					scale={scale}
				/>
			)}

			{(word.subwords != undefined && word.subwords.length > 1) && (
				<Group>
					{word.subwords.map((sub, sw) => {
						if(sw == word.subwords.length - 1) return null;
						acc += parseInt(sub);

						const points_h = [0, 0.5 * cell_size, 0.3 * cell_size, 0.5 * cell_size];
						const points_v = [0.5 * cell_size, 0, 0.5 * cell_size, 0.3 * cell_size];

						return (
							<Line
								key={`word_${orientation}_${word.key_index}_breaker_${sw}`}
								points={(orientation == 'h') ? points_h : points_v}
								stroke={props?.board_color ?? 'black'}
								strokeWidth={1}
								x={(orientation == 'h') ? (Math.random() + (acc - 0.15) * cell_size) : 0}
								y={(orientation == 'v') ? (Math.random() + (acc - 0.15) * cell_size) : 0}
							/>
						)
					})}
				</Group>
			)}
		</Group>
	)
};

const WordDefinitions = ({ h_words, v_words, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ is_loaded, setIsLoaded ] = useState(false);
	const [ pending_words, setPendingWords ] = useState([...Array(h_words.length + v_words.length).keys()]);

	const display_validations = props?.display_validations ?? false;

	useEffect(() => {
		if(props.pending_words.length < 1) return;
		setPendingWords(props.pending_words);
	}, [ props.pending_words ]);

	useEffect(() => {
		waitForElementInDOM('#word_definitions').then((elm) => {
			setIsLoaded(true);
		});
	}, []);

	if(!is_loaded) return null;

	return ReactDOM.createPortal(
		<div>
			<div class="crosswords_definitions">
				<div>
					<strong>{i18n("Across")}:</strong>

					<ul>
						{h_words.map((word, w) => {
							return (
								<li
									key={`word_h_${w}`}
									id={`word_h_${word.key_index}`}
									onClick={() => props.toggleSelectWord(word.key_index, 'h')}
									dangerouslySetInnerHTML={{ __html:word.definition_html }}
								/>
							)
						})}
					</ul>
				</div>

				<div>
					<strong>{i18n("Down")}:</strong>

					<ul>
						{v_words.map((word, w) => {
							return (
								<li
									key={`word_v_${w}`}
									id={`word_v_${word.key_index}`}
									onClick={() => props.toggleSelectWord(word.key_index, 'v')}
									dangerouslySetInnerHTML={{ __html:word.definition_html }}
								/>
							)
						})}
					</ul>
				</div>
			</div>

			{(display_validations) && (
				<div class="total_score">
					<p
						dangerouslySetInnerHTML={{ __html:props.intl.formatMessage({
							id:"Score: {total} points."
						}, {
							total:`<strong id="total_score_val">0</strong>`,
						})}}
					/>
				</div>
			)}
		</div>,
		document.getElementById('word_definitions'),
	);
};

const CrosswordsCanvas = forwardRef((props, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ indexed_items,setIndexedItems ] = useState({});
	const [ reset, setReset ] = useState({ reset:true });
	const [ full_loaded, setFullLoaded ] = useState(false);
	const timer = useRef(null);

	const stage = props?.stage ?? {};
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const min_words = 0;

	const display_validations = stage?.crosswords_display_validations ?? false;
	const enable_word_solving = stage?.crosswords_enable_solve_words ?? false;
	const is_presolved = (is_builder) ? (props?.is_presolved ?? false) : false;
	const is_export = (is_builder) ? (props?.is_export ?? false) : false;

	const promo_colors = props?.promo_colors ?? {};

	const letter_bg_color = stage?.crosswords_cell_color ?? 'rgba(255, 255, 255, 1)';
	const letter_color = stage?.text_color ?? 'rgba(0, 0, 0, 1)';
	const letter_ko_bg_color = stage?.crosswords_wrong_bg_color ?? letter_bg_color;
	const letter_ko_color = stage?.crosswords_wrong_text_color ?? letter_color;
	const letter_ok_bg_color = (display_validations) ? (stage?.crosswords_ok_bg_color ?? promo_colors.ribbon_open_background_color) : letter_bg_color;
	const letter_ok_color = (display_validations) ? (stage?.crosswords_ok_text_color ?? promo_colors.ribbon_open_text_color) : letter_color;
	const letter_active_bg_color = stage?.highlight_color ?? 'rgba(255, 255, 0, 1)';
	const letter_active_color = stage?.highlight_text_color ?? 'rgba(0, 0, 0, 1)';
	const board_bg_color = stage?.bg_color ?? 'rgba(0, 0, 0, 1)';

	const text_direction = props?.text_direction ?? 'ltr';

	const game_score = useRef(0);
	const points_per_word = 10;
	const bonus_points_per_success = stage?.crosswords_points ?? 100;

	const score_message_h = 80;

	// We need to set the initial canvas size to the container, at most, to avoid functionality issues on small devices
	// (apparently, when the canvas gets shrinked more than 30-40% at once, some weird things happen)
	// so we initially set the canvas size to the minimum value between the default (600px) and the body (to fit small device screens):
	const size = useRef(Math.min(standard_board_size, document.querySelector('body').offsetWidth));

	const words = (stage?.crosswords_words ?? []).filter((w) => (isNotEmpty(w, 'word')));

	const actual_puzzle = stage?.crosswords_positions ?? [];

	const game_size = getGridMinSize(generateGrid(actual_puzzle));

	const board_grid_col = Math.min(17, Math.max(12, parseInt(game_size?.col ?? 17)));
	const board_grid_row = Math.min(17, Math.max(12, parseInt(game_size?.row ?? board_grid_col)));

	const calculateItemSize = () => {
		const size_w = (size.current - 2) / board_grid_col;
		const size_h = (size.current - 2) / board_grid_row;

		return Math.min(size_w, size_h);
	};

	const item_size = useRef(calculateItemSize());
	const refs = useRef([]);
	const stoppedGame = useRef(false);
	const selected_word = useRef(false);
	const user_words = useRef({});
	const twin_letters = useRef([]);
	const word_starts = useRef([]);
	const total_letters_in_active_word = useRef(0);
	const missing_words = useRef([]);
	const display_score_anim = useRef(false);
	const hints = useRef({});

	const generateDefaultMissingWords = () => {
		const m = [];

		actual_puzzle.map((w) => {
			m.push({
				word:w.word,
				solved:false,
			});
		})

		return m;
	};

	useEffect(() => {
		missing_words.current = generateDefaultMissingWords();
	}, [ stage.crosswords_positions ]);

	const getWordStartPositions = () => {
		const board_grid_size = Math.max(board_grid_col, board_grid_row);

		const word_start_positions = [];
		
		[...Array(board_grid_size).keys()].map((item_x, col) => {
			word_start_positions.push([]);
	
			[...Array(board_grid_size).keys()].map((item_y, row) => {
				word_start_positions[col].push(0);
			});
		});

		actual_puzzle.map((w) => {
			word_start_positions[w.y][w.x]++;
		});

		return word_start_positions;
	};

	const getAllWordsByOrientation = (orientation) => {
		const selected_words = [];
		
		actual_puzzle.map((word, w) => {
			if(word.orientation != orientation) return;

			let is_hint = false;
			if(hints.current[`_${w}`] != undefined) is_hint = hints.current[`_${w}`];

			selected_words.push({
				...word,
				key_index:w + 1,
				is_hint,
				definition_html:getWordDefinition(word),
			});
		});

		return selected_words.sort((a, b) => {
			return (parseInt(a.word_index) < parseInt(b.word_index)) ? -1 : 1;
		});
	};

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		gameInit();

		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		twin_letters.current = getTwinLetters(actual_puzzle, Math.max(board_grid_col,board_grid_row));
		word_starts.current = getWordStartPositions();

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			exitGame();
		}
	}, []);

	useEffect(() => {
		hints.current = stage?.crosswords_hints ?? 3; 

		if(true || full_loaded) {
			if(refs.current['layer'] != undefined) {
				refs.current['layer'].draw();

				setFullLoaded(true);
			}

			return () => {
				clearTimeout(timer.current);
			}
		}
	}, [ actual_puzzle ]);

	useImperativeHandle(ref, () => ({
		nextWord() {
			goNextWord();
		},

		prevWord() {
			goPrevWord();
		},

		revealWord() {
			revealActualWord();
		},

		stopGame(success_game, cb) {
			stoppedGame.current = true;

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			const cleanup = () => {
				exitGame();

				if(cb != undefined) {
					setTimeout(() => {
						cb(success_game ? (game_score.current > 0 ? 1 :2) : 0);
					}, 200);
				}
			}
			cleanup();
		},
	}), []);
 
	const exitGame = () => {
		if(refs.current[`main_stage`]) {
			refs.current[`main_stage`].removeChildren();
			refs.current[`main_stage`].destroy();
		}
	};

	useEffect(() => {
		Object.keys(indexed_items).map((item) => {
			if(Array.isArray(indexed_items[item])) {
				const new_items = [];
				indexed_items[item].map((it) => {
					new_items.push(adaptImageSizes(it));
				});
				indexed_items[item] = new_items;
			} else {
				indexed_items[item] = adaptImageSizes(indexed_items[item]);
			}
		});

		setIndexedItems({ ...indexed_items });
	}, [ item_size.current ]);

	const gameInit = (from_resize) => {
		if(from_resize == undefined) from_resize = false;

		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current = Math.round(size_received);
				document.querySelector('.gui_game_wrapper').style.width = `${size.current}px`;
				item_size.current = calculateItemSize();
				props.startTimer();
			});
		}

		setTimeout(() => {
			if(window.adaptWidgetHeight != undefined) adaptWidgetHeight();
		}, 500);
	};

	const validateLetter = (letter, letter_ref, twin_letter_ref) => {
		const _needs = letter.attrs.needs;
		const _text = letter.attrs?.text ?? "";

		const solved = (_needs == _text) ? true : false;
		refs.current[letter_ref].setAttrs({
			is_ok:solved,
		});

		if(twin_letter_ref !== false) {
			refs.current[twin_letter_ref.attrs.text_ref].setAttrs({
				is_ok:solved,
			});
		}

		if(display_validations) {
			refs.current[`letter_${refs.current[letter_ref].attrs.col}_${refs.current[letter_ref].attrs.row}_bg`].setAttrs({
				fill:(solved) ? letter_ok_bg_color : letter_ko_bg_color,
			});

			refs.current['layer'].draw();
		}
	};

	const verifyIfAllWordsIn = () => {
		let result = true;
		Object.values(user_words.current).map((w) => {
			if(result && w === false) result = false;
		});

		if(result) enableContinueButton(user_words.current);
		return result;
	};

	const setLetterColor = (letter_ref, color) => {
		if(!(letter_ref.attrs?.is_hint ?? false)) {
			letter_ref.fill(color);
		}

		const twin_letter_ref = getTwinLetterRef(letter_ref, letter_ref.attrs.orientation);
		if(twin_letter_ref !== false) {
			refs.current[twin_letter_ref.attrs.text_ref].fill(color);
		}

		refs.current['layer'].draw();
	};

	const validateGame = (key_index, orientation) => {
		if(refs.current['all_words'] == undefined) return;

		missing_words.current = [];

		const answered = {};
		(refs.current['all_words'].getChildren()).map((word, w) => {
			const word_ref = word.attrs.used_ref;

			const word_ref_parts = word_ref.split("_");
			const word_index = word_ref_parts[1];

			const is_selected_word = (`${orientation}_${key_index}` == word_ref) ? true : false;
			const expected = word.attrs.expected;

			const letters = [];
			word.getChildren().map((_letter, l) => {
				if(!(_letter.attrs?.is_letter ?? false)) return;
				const letter = _letter.find('.text_letter')[0];

				letters.push(letter.text());

				if(is_selected_word) {
					setLetterColor(letter, letter_active_color);
				}
			});

			const solved = (expected == letters.join("").toLowerCase()) ? true : false;
			const all_letters_filled_in = (expected.length == letters.join("").length) ? true : false;

			word.setAttrs({ is_ok:solved });

			missing_words.current[word_index - 1] = {
				word:expected,
				solved,
			};

			if(display_validations) {
				answered[expected] = solved;

				refs.current[`word_bg_${word_ref}`].setAttrs({
					opacity:(is_selected_word) ? 1 : 0,
					is_ok:solved,
				});
			} else {
				answered[expected] = all_letters_filled_in;
			}

			const definition = document.getElementById(`word_${word_ref}`);
			if(all_letters_filled_in) {
				definition.classList.add('answered_word');
			} else {
				definition.classList.remove('answered_word');
			}
		});

		refs.current['layer'].draw();
		user_words.current = answered;

		calculateScore();

		setTimeout(() => {
			verifyIfAllWordsIn();
		}, 300);
	};

	const calculateScore = () => {
		let actual_score = 0;

		// score for each word correctly entered:
		missing_words.current.map((w) => {
			if(w?.solved ?? false) actual_score += points_per_word;
		});

		// bonus for completing all words:
		const has_all_words_solved = missing_words.current.reduce((old, current) => (old && current.solved), true) ? true : false;
		actual_score += (has_all_words_solved) ? parseInt(bonus_points_per_success) : 0;

		if(display_validations && actual_score > game_score.current) {
			display_score_anim.current = true;

			document.querySelector('#total_score_val').innerHTML = actual_score;
		} else {
			display_score_anim.current = false;
		}

		updateScore(actual_score);
	};

	const displayPointIncremental = () => {
		if(!display_score_anim.current) return;
		display_score_anim.current = false;

		refs.current[`new_score`].setAttrs({
			y:size.current + score_message_h + 10,
		});
		
		refs.current[`new_score`].to({
			y:0.5 * (size.current - score_message_h),
			easing:Konva.Easings.StrongEaseOut,
			duration:0.6,
			onFinish:() => {
				setTimeout(() => {
					refs.current[`new_score`].to({
						y:-1 * (score_message_h + 10),
						easing:Konva.Easings.StrongEaseIn,
						duration:0.6,
					});
				}, 300);
			},
		});
	};

	if(is_builder && words.length < min_words) {
		return (
			<div class="canvas_cont not_bordered game_with_errors">
				{props.intl.formatMessage({ id:"At least {min} words are required." }, { min:min_words })}
			</div>
		)
	}

	const getTwinLetterRef = (letter_ref, orientation) => {
		const col = letter_ref.attrs.col;
		const row = letter_ref.attrs.row;

		const twin_letter_ref = refs.current[`_letter_${(orientation == 'h') ? 'v' : 'h'}_${col}_${row}`];

		if(twin_letter_ref != undefined) return twin_letter_ref;
		return false;
	}

	const updateScore = (new_score) => {
		game_score.current = new_score;
		if(props.onScore != undefined) props.onScore(game_score.current);
	};

	const parsePressedLetter = (e) => {
		if(e.key != 'Unidentified') return e.key;

		// we ONLY return 1 letter (this is to fix a bug in Android causing
		// the textareas to concatenate the hint letter to the entered one
		// and then having the textarea with more than 1 letter, breaking the code onwards)
		return e.target.value.split("")[0];
	};

	const setEventsOnTextInputKeyDown = (e, key_index) => {
		const pressed_key = parsePressedLetter(e);

		if(pressed_key == 'Tab' && !e.shiftKey) {
			goNextWord(key_index);
			e.preventDefault();
			return;
		}

		if(pressed_key == 'Tab' && e.shiftKey) {
			goPrevWord(key_index);
			e.preventDefault();
			return;
		}
	};

	const disableLetter = (orientation, key_index, actual_letter) => {
		const letter_ref = refs.current[`letter_${orientation}_${key_index}_${actual_letter}`];
		
		const is_hint = letter_ref.attrs?.is_hint ?? false;
		if(is_hint) return true;

		const twin_letter_ref = getTwinLetterRef(letter_ref, orientation);
		if(twin_letter_ref === false || !display_validations) return false;

		const twin_letter_word_ref_parts = twin_letter_ref.attrs.text_ref.split("_");
		const twin_letter_word_ref = refs.current[`word_${twin_letter_word_ref_parts[1]}_${twin_letter_word_ref_parts[2]}`];
		return twin_letter_word_ref.attrs?.is_ok ?? false;
	};

	const setEventsOnTextInputKeyUp = (e, key_index, orientation) => {
		let prev_letter_arrow = (orientation == 'h') ? 'ArrowLeft' : 'ArrowUp';
		let next_letter_arrow = (orientation == 'h') ? 'ArrowRight' : 'ArrowDown';
		if(text_direction == 'rtl' && orientation == 'h') {
			prev_letter_arrow = 'ArrowRight';
			next_letter_arrow = 'ArrowLeft';
		}

		const pressed_key = parsePressedLetter(e);

		const actual_letter_textarea = parseInt(e.target.id.split("_")[1]);
		const actual_letter = parseInt(e.target.dataset.letter);
		const total_letters = parseInt(e.target.dataset.total);

		const letter_ref = refs.current[`letter_${orientation}_${key_index}_${actual_letter}`];

		const twin_letter_ref = getTwinLetterRef(letter_ref, orientation);
		let _letter_ref;
		if(twin_letter_ref !== false) {
			_letter_ref = refs.current[twin_letter_ref.attrs.text_ref];
		}

		const disable_letter = disableLetter(orientation, key_index, actual_letter);

		if(pressed_key == 'Backspace') {
			let prev_letter = actual_letter - 1;
			if(prev_letter < 0) prev_letter = total_letters - 1;

			const actual_textarea = document.getElementById(`letter_${actual_letter}`);
			const letter_to_remove_ref = refs.current[`letter_${orientation}_${key_index}_${actual_letter}`];

			if(!disable_letter && letter_to_remove_ref.text() != '') {
				actual_textarea.value = '';
				letter_to_remove_ref.text('');
				if(twin_letter_ref !== false) _letter_ref.text('');

				validateLetter(letter_ref, `letter_${orientation}_${key_index}_${actual_letter}`, twin_letter_ref);
				validateGame(key_index, orientation);

				refs.current['layer'].draw();
			} else {
				// Otherwise, go to previous letter:
				goPrevLetterInWord(actual_letter_textarea);
				e.preventDefault();
			}
			return;
		}

		if(pressed_key == prev_letter_arrow) {
			goPrevLetterInWord(actual_letter_textarea);
			e.preventDefault();
			return;
		}

		if(pressed_key == next_letter_arrow) {
			goNextLetterInWord(actual_letter_textarea);
			e.preventDefault();
			return;
		}

		if(pressed_key == 'Enter') {
			goNextWord(key_index);
			e.preventDefault();
		}

		if(e.altKey || e.metaKey || e.ctrlKey) {
			return;
		}

		if(ensureAlfanumericCharacter(pressed_key)) {
			if(disable_letter) {
				setTimeout(() => {
					goNextLetterInWord(actual_letter_textarea);
				}, 0);
	
				return;
			}
	
			const actual_letter_str = pressed_key.toUpperCase();
			letter_ref.text(actual_letter_str);
			if(twin_letter_ref !== false) _letter_ref.text(actual_letter_str);

			validateLetter(letter_ref, `letter_${orientation}_${key_index}_${actual_letter}`, twin_letter_ref);
			validateGame(key_index, orientation);

			refs.current['layer'].draw();

			setTimeout(() => {
				goNextLetterInWord(actual_letter_textarea);
			}, 0);
		}
	};

	const getPendingWords = () => {
		const pending_words = missing_words.current.filter((w) => {
			return (w.solved === false) ? true : false;
		});

		return pending_words;
	};

	const getPendingLetters = (key_index, orientation) => {
		const ref = refs.current[`word_${orientation}_${key_index}`];

		const pending_letters = [];
		ref.getChildren().map((_letter, l) => {
			if(!(_letter.attrs?.is_letter ?? false)) return;
			const letter = _letter.find('.text_letter')[0];

			const is_hint = letter.attrs?.is_hint ?? false;
			const is_ok = (display_validations) ? (letter.attrs?.is_ok ?? false) : false;

			if((!is_hint && !is_ok) || true) pending_letters.push(l);
		});

		return pending_letters;
	};

	const addTextInputsInWord = (key_index, orientation, letter_index = 0) => {
		const ref = refs.current[`word_${orientation}_${key_index}`];

		validateGame(key_index, orientation);

		//if(user_words.current[ref.attrs.expected] === true) return;

		// at first lets find position of text node relative to the stage:
		const word_position = ref.getAbsolutePosition(refs.current[`layer`]);

		const pending_letters = getPendingLetters(key_index, orientation);

		total_letters_in_active_word.current = pending_letters.length;

		if(letter_index > 0 && !pending_letters.includes(letter_index)) {
			// we clicked on a specific letter, but it's already a correct letter,
			// so we select the first empty / wrong letter in the word:
			letter_index = 0;
		}

		if(letter_index > 0 && pending_letters.includes(letter_index)) {
			// we clicked on a specific letter, and it's still an empty / wrong letter,
			// so we select it:
			letter_index = pending_letters.indexOf(letter_index);
		}

		let _l = 0;
		[...Array(parseInt(ref.attrs.total_letters)).keys()].map((letter, l) => {
			const letter_ref = refs.current[`letter_${orientation}_${key_index}_${l}`];

			const textarea = document.createElement('textarea');
			document.querySelector('.canvas_content').appendChild(textarea);

			textarea.value = letter_ref.text();

			textarea.style.height = `${item_size.current}px`;
			textarea.style.width = `${item_size.current}px`;

			textarea.style.top = (orientation == 'h') ? `${word_position.y}px` : `${word_position.y + l * item_size.current}px`;
			textarea.style.left = (orientation == 'h') ? `${word_position.x + l * item_size.current}px` : `${word_position.x}px`;

			textarea.classList.add('letter_input');
			textarea.setAttribute('id', `letter_${_l}`);
			textarea.setAttribute('autocapitalize', 'characters');
			textarea.setAttribute('data-letter', l);
			textarea.setAttribute('data-total', ref.attrs.total_letters);

			if(pending_letters[letter_index] == l) textarea.focus();

			// We need the event onkeydown to enable two keys at the same time (shift + other):
			textarea.addEventListener('keydown', (e) => {
				setEventsOnTextInputKeyDown(e, key_index);
			});

			// We need the event onkeyup to enable devices like Android to detect the change on the value:
			textarea.addEventListener('keyup', (e) => {
				setEventsOnTextInputKeyUp(e, key_index, orientation);
			});

			_l++;
		});
	};

	const getKeyWordFromSelected = () => {
		if(selected_word.current === false) return false;
		return parseInt(selected_word.current.split("_")[1]);
	};

	const enableContinueButton = () => {
		blurWord();
		props.gameDispatch({ type:'SET_READY_TO_SEND' });
	};

	const revealActualWord = (key_index) => {
		if(key_index == undefined) key_index = getKeyWordFromSelected();
		if(key_index === false) return;

		const actual_word = actual_puzzle[key_index - 1];
		let orientation = actual_word.attrs?.orientation ?? 'h';
		let ref = refs.current[`word_${orientation}_${key_index}`];

		if(ref == undefined) {
			orientation = (orientation == 'h') ? 'v' : 'h';
			ref = refs.current[`word_${orientation}_${key_index}`];
		}

		[...Array(parseInt(ref.attrs.total_letters)).keys()].map((letter, l) => {
			const letter_ref = refs.current[`letter_${orientation}_${key_index}_${l}`];

			letter_ref.setAttrs({
				text:letter_ref.attrs.needs,
			});

			const twin_letter_ref = getTwinLetterRef(letter_ref, orientation);
			if(twin_letter_ref !== false) {
				const _letter_ref = refs.current[twin_letter_ref.attrs.text_ref];
				_letter_ref.text(letter_ref.attrs.needs);
			}

			validateLetter(letter_ref, `letter_${orientation}_${key_index}_${l}`, twin_letter_ref);
		});

		validateGame(key_index, orientation);
		goNextWord(key_index);
	};

	const goPrevWord = (key_index) => {
		const pending_words = getPendingWords();

		if(pending_words.length < 1) {
			blurWord();
			return;
		}

		if(key_index == undefined) key_index = getKeyWordFromSelected();
		if(key_index === false) return;

		let prev_key_index = key_index - 1;
		if(prev_key_index < 1) prev_key_index = missing_words.current.length;

		// don't loop between answered and visually correct words:
		if(missing_words.current[prev_key_index - 1].solved === true) {
			goPrevWord(prev_key_index);
			return;
		}

		const prev_word = actual_puzzle[prev_key_index - 1];
		toggleSelectWord(prev_key_index, prev_word.orientation);
	};

	const goNextWord = (key_index) => {
		const pending_words = getPendingWords();

		if(pending_words.length < 1) {
			blurWord();
			return;
		}

		if(key_index == undefined) key_index = getKeyWordFromSelected();
		if(key_index === false) return;
		let next_key_index = key_index + 1;
		if(key_index >= missing_words.current.length) next_key_index = 1;

		// don't loop between answered and visually correct words:
		if(missing_words.current[next_key_index - 1].solved === true) {
			goNextWord(next_key_index);
			return;
		}

		const next_word = actual_puzzle[next_key_index - 1];
		toggleSelectWord(next_key_index, next_word.orientation);
	};

	const goPrevLetterInWord = (actual_letter) => {
		let prev_letter = actual_letter - 1;
		if(prev_letter < 0) prev_letter = total_letters_in_active_word.current - 1;
		const letter = document.querySelector(`#letter_${prev_letter}`);
		if(letter != undefined) letter.focus();
	};

	const goNextLetterInWord = (actual_letter) => {
		let next_letter = actual_letter + 1;
		if(next_letter >= total_letters_in_active_word.current) next_letter = 0;
		const letter = document.querySelector(`#letter_${next_letter}`);
		if(letter != undefined) letter.focus();
	};

	const removeTextareas = () => {
		const textareas = document.getElementsByClassName('letter_input');
		while(textareas[0]) {
			textareas[0].removeEventListener('keyup', setEventsOnTextInputKeyUp);
			textareas[0].removeEventListener('keydown', setEventsOnTextInputKeyDown);
			textareas[0].parentNode.removeChild(textareas[0]);
		}
	};

	const getWordCode = (_word) => {
		const orientation = (false) ? ((_word.orientation == 'h') ? "A" : "D") : "";
		return `${i18n(orientation)}${(false && parseInt(_word.word_index) < 10) ? `0${_word.word_index}` : _word.word_index}`;
	};

	const getWordDefinition = (_word) => {
		return `<span><strong>${getWordCode(_word)}:</strong> ${_word?.definition ?? `-- ${i18n("Missing definition")} --`} <span style="white-space:nowrap;">[${_word.subwords.join("-")}]</span></span>`;
	};

	const setLetterColors = () => {
		refs.current[`word_${selected_word.current}`].getChildren().map((_letter, l) => {
			if(!(_letter.attrs?.is_letter ?? false)) return;
			const letter = _letter.find('.text_letter')[0];

			const _fill = (letter.attrs?.is_ok ?? false) ? letter_ok_color : letter_ko_color;

			setLetterColor(letter, _fill);
		});

		refs.current[`layer`].draw();
	};

	const blurWord = () => {
		if(selected_word.current === false) return;

		removeTextareas();

		refs.current[`word_bg_${selected_word.current}`].opacity(0);
		setLetterColors();

		document.querySelectorAll('.top_definition').forEach((item) => {
			item.innerHTML = i18n("Click on a square");
		});
		selected_word.current = false;

		if(props.hide_solve_word != undefined) {
			props.hide_solve_word();
		}

		refs.current[`layer`].draw();
	};

	const displayActualWordInBoard = (key_index, orientation) => {
		const pending_letters = getPendingLetters(key_index, orientation);

		refs.current[`word_bg_${selected_word.current}`].getChildren().map((_letter, l) => {
			_letter.attrs.opacity = (pending_letters.includes(l)) ? 1 : 0.2;
		});

		refs.current[`word_bg_${selected_word.current}`].moveToTop();
		refs.current[`word_bg_${selected_word.current}`].opacity(1);
	};

	const toggleSelectWord = (key_index, orientation, letter_index = 0) => {
		displayPointIncremental();
		blurWord();

		if(key_index != selected_word.current) {
			if(props.hide_solve_word != undefined) {
				props.hide_solve_word();
			}

			selected_word.current = `${orientation}_${key_index}`;

			displayActualWordInBoard(key_index, orientation);
			addTextInputsInWord(key_index, orientation, letter_index);

			document.querySelectorAll('.top_definition').forEach((item) => {
				item.innerHTML = getWordDefinition(actual_puzzle[key_index - 1]);
			});

			if(enable_word_solving && props.display_solve_word != undefined) {
				setTimeout(() => {
					if(selected_word.current !== false) props.display_solve_word();
				}, 5000);
			}
		}

		refs.current[`layer`].draw();
	};

	const toggleSelectWordConditional = (key_index, orientation, letter_index = 0) => {
		const ref = refs.current[`word_${orientation}_${key_index}`];

		const is_ok = ref.attrs?.is_ok ?? false;
		if(display_validations && is_ok) return;

		toggleSelectWord(key_index, orientation, letter_index);
	};

	const stage_classes = ['canvas_cont', 'not_bordered'];

	const canvas_scale = size.current / standard_board_size;

	return (
		<>
			<Stage
				ref={(node) => { refs.current[`main_stage`] = node; }}
				className={stage_classes.join(" ")}
				width={size.current}
				height={size.current}
			>
				<Layer ref={(node) => { refs.current[`layer`] = node; }}>
					<Rect
						width={size.current}
						height={size.current}
						fill={board_bg_color}
						onMouseDown={blurWord}
						onTouchStart={blurWord}
					/>

					<Group
						x={(size.current - game_size.col * item_size.current) / 2 - 1}
						y={(size.current - game_size.row * item_size.current) / 2 - 1}
					>
						<Group x={1} y={1}>
							{(twin_letters?.current ?? []).map((row, r) => {
								return (
									<>
										{row.map((col, c) => {
											if(parseInt(twin_letters.current[r][c]) < 1) return null;

											return (
												<Rect
													key={`letter_${c}_${r}_bg`}
													ref={(node) => { refs.current[`letter_${c}_${r}_bg`] = node; }}
													x={c * item_size.current}
													y={r * item_size.current}
													width={item_size.current}
													height={item_size.current}
													fill={letter_bg_color}
												/>
											)
										})}
									</>
								)
							})}
						</Group>

						<Group x={1} y={1}>
							{getAllWordsByOrientation('v').map((word) => {
								return (
									<WordBg
										key={`word_v_bg_${word.key_index}`}
										word={word}
										orientation='v'
										refs={refs.current}
										cell_size={item_size.current}
										color={letter_active_bg_color}
										ok_bg_color={letter_ok_bg_color}
										ok_text_color={letter_ok_color}
										display_validations={display_validations}
									/>
								)
							})}

							{getAllWordsByOrientation('h').map((word) => {
								return (
									<WordBg
										key={`word_h_bg_${word.key_index}`}
										word={word}
										orientation='h'
										refs={refs.current}
										cell_size={item_size.current}
										color={letter_active_bg_color}
										ok_bg_color={letter_ok_bg_color}
										ok_text_color={letter_ok_color}
										display_validations={display_validations}
									/>
								)
							})}
						</Group>

						<Group x={1} y={1} ref={(node) => { refs.current[`all_words`] = node; }}>
							{getAllWordsByOrientation('v').map((word, w) => {
								return (
									<Word
										key={`word_v_${w}`}
										word={word}
										orientation='v'
										refs={refs.current}
										cell_size={item_size.current}
										toggleSelectWord={toggleSelectWordConditional}
										text_color={letter_color}
										board_color={board_bg_color}
										bg_color={letter_bg_color}
										ok_bg_color={letter_ok_bg_color}
										ok_text_color={letter_ok_color}
										scale={canvas_scale}
										twins={twin_letters?.current ?? []}
										word_starts={word_starts?.current ?? []}
										is_presolved={is_presolved}
										display_validations={display_validations}
									/>
								)
							})}

							{getAllWordsByOrientation('h').map((word, w) => {
								return (
									<Word
										key={`word_h_${w}`}
										word={word}
										orientation='h'
										refs={refs.current}
										cell_size={item_size.current}
										toggleSelectWord={toggleSelectWordConditional}
										text_color={letter_color}
										board_color={board_bg_color}
										bg_color={letter_bg_color}
										ok_bg_color={letter_ok_bg_color}
										ok_text_color={letter_ok_color}
										scale={canvas_scale}
										twins={twin_letters?.current ?? []}
										word_starts={word_starts?.current ?? []}
										is_presolved={is_presolved}
										display_validations={display_validations}
									/>
								)
							})}
						</Group>

						<Line
							points={[0, 0, 0, size.current]}
							stroke={board_bg_color}
							strokeWidth={4}
						/>

						<Line
							points={[size.current, 0, size.current, size.current]}
							stroke={board_bg_color}
							strokeWidth={4}
						/>

						<Line
							points={[0, 0, size.current, 0]}
							stroke={board_bg_color}
							strokeWidth={4}
						/>

						<Line
							points={[0, size.current, size.current, size.current]}
							stroke={board_bg_color}
							strokeWidth={4}
						/>

						<Group x={1} y={1}>
							{[...Array(board_grid_col).keys()].map((item_x, col) => {
								return (
									<Line
										key={`line_${col}`}
										points={[col * item_size.current, 0, col * item_size.current, size.current]}
										stroke={board_bg_color}
										strokeWidth={1}
									/>
								)
							})}

							{[...Array(board_grid_row).keys()].map((item_y, row) => {
								return (
									<Line
										key={`line_${row}`}
										points={[0, row * item_size.current, size.current, row * item_size.current]}
										stroke={board_bg_color}
										strokeWidth={1}
									/>
								)
							})}
						</Group>
					</Group>

					<Group
						ref={(node) => { refs.current[`new_score`] = node; }}
						y={size.current + score_message_h + 10}
					>
						<Rect
							width={size.current}
							height={score_message_h}
							fill={letter_ok_bg_color}
						/>

						<Text
							width={size.current}
							height={score_message_h}
							text={`+${points_per_word}`}
							fontSize={35}
							fill='white'
							align='center'
							verticalAlign='middle'
							stroke='white'
							strokeWidth={5}
							lineCap='round'
							lineJoin='round'
						/>

						<Text
							width={size.current}
							height={score_message_h}
							text={`+${points_per_word}`}
							fontSize={35}
							fill='black'
							align='center'
							verticalAlign='middle'
						/>
					</Group>
				</Layer>
			</Stage>

			{(!is_export) && (
				<WordDefinitions
					h_words={getAllWordsByOrientation('h')}
					v_words={getAllWordsByOrientation('v')}
					pending_words={missing_words.current}
					toggleSelectWord={toggleSelectWord}
					display_validations={display_validations}
					intl={props.intl}
				/>
			)}
		</>
	)
})

export default CrosswordsCanvas;